import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Download, Share2 } from "lucide-react";

const PILLAR_LABELS = { zh: ["年", "月", "日", "时"], en: ["Y", "M", "D", "H"] };

// Classical destiny name card: 命局总述 + zodiac/day master/four pillars.
export default function NameCard({ name, chart, overall }) {
  const { t, lang } = useI18n();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const p = chart?.pillars || {};
  const pillars = ["year", "month", "day", "time"].map((k) => (p[k]?.ganZhi || ""));

  const render = async () => {
    const node = cardRef.current;
    if (!node) return null;
    return toPng(node, { pixelRatio: 2, cacheBust: true, backgroundColor: "#f6efe0" });
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = await render();
      if (!url) return;
      const a = document.createElement("a");
      a.download = `CiAO-Merit-${(name || "chart").replace(/\s+/g, "")}.png`;
      a.href = url;
      a.click();
      toast.success(t("card.downloaded"));
    } catch (e) {
      toast.error(t("card.exportError"));
    } finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const url = await render();
      const text = `${t("card.shareText")} — ${name || ""}\n${(overall || "").slice(0, 80)}`;
      // Prefer native share with the image file when available (mobile)
      if (url && navigator.canShare) {
        try {
          const blob = await (await fetch(url)).blob();
          const file = new File([blob], "ciao-merit.png", { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], text });
            setBusy(false);
            return;
          }
        } catch (_) { /* fall through */ }
      }
      // Fallback: download the image and open WhatsApp with the text
      if (url) {
        const a = document.createElement("a");
        a.download = `CiAO-Merit-${(name || "chart").replace(/\s+/g, "")}.png`;
        a.href = url; a.click();
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
      toast.info(t("card.shareHint"));
    } catch (e) {
      toast.error(t("card.exportError"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="name-card">
      {/* The card surface (exported to image) */}
      <div className="flex justify-center">
        <div
          ref={cardRef}
          className="relative w-[340px] rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(160deg, #f8f2e4 0%, #efe4cd 100%)",
            border: "2px solid hsl(42 62% 52%)",
            boxShadow: "0 10px 30px rgba(120,90,30,0.18)",
          }}
        >
          {/* inner gold rule */}
          <div className="absolute inset-2 rounded-xl pointer-events-none" style={{ border: "1px solid hsl(42 55% 62% / 0.7)" }} />
          {/* seal */}
          <div className="absolute top-4 right-4 h-10 w-10 rounded-md flex items-center justify-center" style={{ background: "hsl(6 72% 45%)" }}>
            <span className="font-display text-white text-lg leading-none">命</span>
          </div>

          <div className="relative px-6 pt-6 pb-5">
            <div className="text-[11px] tracking-[0.3em] text-[hsl(var(--brand-bronze))]">CIAO MERIT · 你好功德</div>
            <div className="font-display text-2xl font-bold text-[hsl(var(--brand-ink))] mt-1">{name || t("card.title")}</div>

            <div className="flex items-center gap-2 mt-3">
              <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: "hsl(42 55% 72%)", color: "hsl(30 40% 22%)" }}>
                {t("card.zodiac")} {chart?.zodiac}
              </span>
              <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: "hsl(42 55% 72%)", color: "hsl(30 40% 22%)" }}>
                {t("card.dayMaster")} {chart?.day_master}
              </span>
            </div>

            {/* Four pillars */}
            <div className="grid grid-cols-4 gap-2 mt-4">
              {pillars.map((gz, i) => (
                <div key={i} className="rounded-lg text-center py-2" style={{ background: "hsl(0 0% 100% / 0.6)", border: "1px solid hsl(42 45% 70%)" }}>
                  <div className="text-[10px] text-[hsl(var(--brand-bronze))]">{PILLAR_LABELS[lang === "zh" ? "zh" : "en"][i]}</div>
                  <div className="font-display font-bold text-[hsl(var(--brand-ink))] leading-tight mt-0.5">
                    {gz.split("").map((ch, j) => <div key={j}>{ch}</div>)}
                  </div>
                </div>
              ))}
            </div>

            {/* Overall */}
            <div className="mt-4 text-[13px] leading-6 text-[hsl(30_40%_25%)] whitespace-pre-line" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              {overall || t("card.placeholder")}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-[10px] text-[hsl(var(--brand-bronze))]">{chart?.lunar_date}</span>
              <span className="text-[10px] text-[hsl(var(--brand-bronze))]">ciaomerit.com</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-2 mt-4">
        <Button variant="outline" className="border-[hsl(var(--brand-gold))]" disabled={busy} onClick={download} data-testid="card-download">
          <Download className="h-4 w-4 mr-1" />{t("card.download")}
        </Button>
        <Button className="bg-[hsl(var(--primary))] text-primary-foreground" disabled={busy} onClick={share} data-testid="card-share">
          <Share2 className="h-4 w-4 mr-1" />{t("card.share")}
        </Button>
      </div>
    </div>
  );
}
